import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './client.js';
import type { Approval, Build, BuildEvent, Deployment, ProjectUsage } from './types.js';

export function useUsage(projectId: string) {
  return useQuery({
    queryKey: ['usage', projectId],
    queryFn: () =>
      apiFetch<{ usage: ProjectUsage }>(`/projects/${projectId}/usage`).then((r) => r.usage),
  });
}

export function useBuilds(projectId: string) {
  return useQuery({
    queryKey: ['builds', projectId],
    queryFn: () =>
      apiFetch<{ builds: Build[] }>(`/projects/${projectId}/builds`).then((r) => r.builds),
    // No project selected yet (e.g. the debug console before a pick) — don't fetch.
    enabled: projectId.length > 0,
  });
}

export function useStartBuild(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { storyId: string; autoApprove?: boolean }) =>
      apiFetch<{ build: Build }>(`/projects/${projectId}/stories/${input.storyId}/build`, {
        method: 'POST',
        body: { autoApprove: input.autoApprove ?? false },
      }).then((r) => r.build),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['builds', projectId] });
      void qc.invalidateQueries({ queryKey: ['stories', projectId] });
    },
  });
}

/** Queues builds for several stories at once (built one after another). */
export function useBatchBuild(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { storyIds: string[]; autoApprove?: boolean }) =>
      apiFetch<{ builds: Build[] }>(`/projects/${projectId}/builds`, {
        method: 'POST',
        body: { storyIds: input.storyIds, autoApprove: input.autoApprove ?? false },
      }).then((r) => r.builds),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['builds', projectId] });
      void qc.invalidateQueries({ queryKey: ['stories', projectId] });
    },
  });
}

/**
 * A project's hosting status. Polls while a deploy is in flight (provisioning or
 * building) so the live URL and status appear on their own once it settles.
 */
export function useDeployment(projectId: string) {
  return useQuery({
    queryKey: ['deployment', projectId],
    queryFn: () =>
      apiFetch<{ deployment: Deployment }>(`/projects/${projectId}/deployment`).then(
        (r) => r.deployment,
      ),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'provisioning' || status === 'building' ? 4000 : false;
    },
  });
}

/** Queues a manual redeploy of the project's app (staff only). */
export function useRedeploy(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ deployment: Deployment }>(`/projects/${projectId}/redeploy`, {
        method: 'POST',
      }).then((r) => r.deployment),
    onSuccess: (deployment) => {
      qc.setQueryData(['deployment', projectId], deployment);
      void qc.invalidateQueries({ queryKey: ['deployment', projectId] });
    },
  });
}

interface BuildDetail {
  build: Build;
  events: BuildEvent[];
  approvals: Approval[];
}

/**
 * One build's live state. Polls while the build is active (an approval is
 * pending or work is in flight), and stops once it reaches a terminal status —
 * the API relays from Postgres, so this is a cheap read, not an Anthropic call.
 */
export function useBuildDetail(projectId: string, buildId: string) {
  return useQuery({
    queryKey: ['build', projectId, buildId],
    queryFn: () => apiFetch<BuildDetail>(`/projects/${projectId}/builds/${buildId}/events`),
    refetchInterval: (query) => {
      const status = query.state.data?.build.status;
      const done = status === 'succeeded' || status === 'failed' || status === 'interrupted';
      return done ? false : 2000;
    },
  });
}

export function useDecideApproval(projectId: string, buildId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      approvalId: string;
      result: 'allow' | 'deny';
      denyMessage?: string;
      always?: boolean;
    }) =>
      apiFetch<{ ok: true }>(
        `/projects/${projectId}/builds/${buildId}/approvals/${input.approvalId}`,
        {
          method: 'POST',
          body: {
            result: input.result,
            ...(input.denyMessage ? { denyMessage: input.denyMessage } : {}),
            ...(input.always ? { always: true } : {}),
          },
        },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['build', projectId, buildId] }),
  });
}
